import React, { useEffect, useState } from 'react';
import { Pencil, Trash2, UserPlus } from 'lucide-react';
import { API_ENDPOINTS } from '../../config/constants';
import { generateToken, TOKEN_HEADER } from '../../utils/tokenUtils';
import './MeetupGroup.css';

const PAGE_SIZE = 30;
const ATTENDEE_STATUSES = ['Registered', 'Attended', 'No Show', 'Cancelled'];
const EMPTY_ATTENDEE = {
  event_id: '', meetup_member_id: '', name: '', title: '', email: '', location: '',
  experience_level: '', current_role: '', joining_purpose: '', event_host: false,
  rsvp: 'Yes', guests: 0, rsvped_on: '', phone_number: '', whatsapp_consent: '', status: 'Registered'
};

function toForm(attendee) {
  return {
    ...EMPTY_ATTENDEE,
    ...attendee,
    rsvped_on: attendee?.rsvped_on ? String(attendee.rsvped_on).slice(0, 16).replace(' ', 'T') : ''
  };
}

function MeetupAttendees() {
  const [attendees, setAttendees] = useState([]);
  const [events, setEvents] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 0, total: 0 });
  const [page, setPage] = useState(1);
  const [eventId, setEventId] = useState('');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_ATTENDEE);
  const [formOpen, setFormOpen] = useState(false);
  const [statusSavingId, setStatusSavingId] = useState(null);

  const authHeaders = () => ({ [TOKEN_HEADER]: generateToken() });

  const fetchEvents = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/admin/meetup-events?limit=100&page=1`, { headers: authHeaders() });
      const payload = await response.json();
      if (response.ok && payload.success) setEvents(payload.data);
    } catch (fetchError) { setError(fetchError.message); }
  };

  const fetchAttendees = async (targetPage = page) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page: targetPage, limit: PAGE_SIZE });
      if (eventId) params.set('eventId', eventId);
      if (status) params.set('status', status);
      if (search.trim()) params.set('search', search.trim());
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/admin/meetup-attendees?${params}`, { headers: authHeaders() });
      const payload = await response.json();
      if (!response.ok || !payload.success) throw new Error(payload.message || 'Failed to load Meetup attendees');
      setAttendees(payload.data);
      setPagination(payload.pagination);
      setPage(targetPage);
    } catch (fetchError) { setError(fetchError.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchEvents(); fetchAttendees(1); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const openForm = (attendee = null) => {
    setEditing(attendee);
    setForm(toForm(attendee));
    setFormOpen(true);
  };

  const payloadFor = (value) => ({
    ...value,
    event_id: Number(value.event_id),
    meetup_member_id: String(value.meetup_member_id),
    guests: Number(value.guests) || 0,
    rsvped_on: value.rsvped_on ? `${value.rsvped_on.replace('T', ' ')}:00` : null
  });

  const saveAttendee = async (submitEvent) => {
    submitEvent.preventDefault();
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/admin/meetup-attendees${editing ? `/${editing.id}` : ''}`, {
        method: editing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(payloadFor(form))
      });
      const payload = await response.json();
      if (!response.ok || !payload.success) throw new Error(payload.message || 'Failed to save attendee');
      setFormOpen(false);
      await fetchAttendees(editing ? page : 1);
    } catch (saveError) { setError(saveError.message); }
  };

  const updateStatus = async (attendee, nextStatus) => {
    setStatusSavingId(attendee.id);
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/admin/meetup-attendees/${attendee.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(payloadFor({ ...attendee, status: nextStatus }))
      });
      const payload = await response.json();
      if (!response.ok || !payload.success) throw new Error(payload.message || 'Failed to update attendee status');
      setAttendees((current) => current.map((item) => item.id === attendee.id ? { ...item, ...payload.data } : item));
    } catch (statusError) { setError(statusError.message); }
    finally { setStatusSavingId(null); }
  };

  const deleteAttendee = async (attendee) => {
    if (!window.confirm(`Delete ${attendee.name} from this event?`)) return;
    const response = await fetch(`${API_ENDPOINTS.BASE_URL}/admin/meetup-attendees/${attendee.id}`, { method: 'DELETE', headers: authHeaders() });
    const payload = await response.json();
    if (!response.ok || !payload.success) return setError(payload.message || 'Failed to delete attendee');
    fetchAttendees(attendees.length === 1 && page > 1 ? page - 1 : page);
  };

  return (
    <section className="meetup-group" aria-labelledby="meetup-attendees-title">
      <div className="meetup-toolbar"><div><h2 id="meetup-attendees-title">Meetup event attendees</h2><p>{pagination.total} registrations</p></div><div className="meetup-toolbar-actions"><button className="meetup-primary" onClick={() => openForm()}><UserPlus size={16} /> Add attendee</button></div></div>
      <form className="meetup-filters" onSubmit={(event) => { event.preventDefault(); fetchAttendees(1); }}>
        <label><span>Search</span><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Name, email, or phone" /></label>
        <label><span>Event</span><select value={eventId} onChange={(event) => setEventId(event.target.value)}><option value="">All events</option>{events.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
        <label><span>Status</span><select value={status} onChange={(event) => setStatus(event.target.value)}><option value="">All statuses</option>{ATTENDEE_STATUSES.map((item) => <option key={item}>{item}</option>)}</select></label>
        <button type="submit">Apply</button>
      </form>
      {error && <div className="meetup-error" role="alert">{error}</div>}
      <div className="meetup-table-wrap"><table className="admin-table meetup-table meetup-attendees-table"><thead><tr><th>Attendee</th><th>Event</th><th>Registration</th><th>Role & purpose</th><th>WhatsApp</th><th>Status</th><th aria-label="Actions" /></tr></thead><tbody>
        {!loading && attendees.map((attendee) => <tr key={attendee.id}>
          <td><strong>{attendee.name}</strong><small>{attendee.email || attendee.phone_number || `ID ${attendee.meetup_member_id}`}</small></td>
          <td><span className="meetup-primary-text">{attendee.event_name}</span><small>{attendee.event_date ? String(attendee.event_date).slice(0, 10) : ''}</small></td>
          <td><span className="meetup-primary-text">RSVP: {attendee.rsvp}</span><small>{attendee.rsvped_on ? String(attendee.rsvped_on).replace('T', ' ').slice(0, 16) : 'No timestamp'} · {attendee.guests || 0} guests</small></td>
          <td><span className="meetup-primary-text">{attendee.current_role || 'Not provided'}</span><small title={attendee.joining_purpose || ''}>{attendee.experience_level ? `Experience: ${attendee.experience_level}` : 'No experience response'}</small></td>
          <td><small>{attendee.whatsapp_consent || '-'}</small></td>
          <td><select className={`meetup-attendee-status meetup-attendee-status-${attendee.status?.toLowerCase().replace(' ', '-')}`} value={attendee.status} disabled={statusSavingId === attendee.id} aria-label={`Status for ${attendee.name}`} onChange={(event) => updateStatus(attendee, event.target.value)}>{ATTENDEE_STATUSES.map((item) => <option key={item}>{item}</option>)}</select></td>
          <td className="meetup-actions"><button className="meetup-icon-button" title="Edit attendee" aria-label={`Edit ${attendee.name}`} onClick={() => openForm(attendee)}><Pencil size={16} /></button><button className="meetup-icon-button meetup-danger" title="Delete attendee" aria-label={`Delete ${attendee.name}`} onClick={() => deleteAttendee(attendee)}><Trash2 size={16} /></button></td>
        </tr>)}
        {!loading && !attendees.length && <tr><td colSpan="7" className="meetup-empty">No event attendees match these filters.</td></tr>}
        {loading && <tr><td colSpan="7" className="meetup-empty">Loading attendees...</td></tr>}
      </tbody></table></div>
      <div className="meetup-pagination"><button disabled={page <= 1 || loading} onClick={() => fetchAttendees(page - 1)}>Previous</button><span>Page {pagination.page} of {Math.max(pagination.pages, 1)}</span><button disabled={page >= pagination.pages || loading} onClick={() => fetchAttendees(page + 1)}>Next</button></div>
      {formOpen && <div className="modal-overlay" onMouseDown={(event) => event.target === event.currentTarget && setFormOpen(false)}><div className="modal-content meetup-modal" role="dialog" aria-modal="true"><div className="meetup-modal-header"><h3>{editing ? 'Edit attendee' : 'Add attendee'}</h3><button aria-label="Close" onClick={() => setFormOpen(false)}>×</button></div><form onSubmit={saveAttendee}><div className="meetup-form-grid">
        <label>Event<select required value={form.event_id} onChange={(event) => setForm({ ...form, event_id: event.target.value })}><option value="">Select event</option>{events.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
        <label>Meetup Member ID<input required type="number" min="1" value={form.meetup_member_id} onChange={(event) => setForm({ ...form, meetup_member_id: event.target.value })} /></label>
        <label>Name<input required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></label>
        <label>Email<input type="email" value={form.email || ''} onChange={(event) => setForm({ ...form, email: event.target.value })} /></label>
        <label>Phone<input value={form.phone_number || ''} onChange={(event) => setForm({ ...form, phone_number: event.target.value })} /></label>
        <label>Location<input value={form.location || ''} onChange={(event) => setForm({ ...form, location: event.target.value })} /></label>
        <label>Experience level<input value={form.experience_level || ''} onChange={(event) => setForm({ ...form, experience_level: event.target.value })} /></label>
        <label>Current role<input value={form.current_role || ''} onChange={(event) => setForm({ ...form, current_role: event.target.value })} /></label>
        <label>RSVP<select value={form.rsvp} onChange={(event) => setForm({ ...form, rsvp: event.target.value })}><option>Yes</option><option>No</option><option>Waitlist</option></select></label>
        <label>RSVPed on<input type="datetime-local" value={form.rsvped_on || ''} onChange={(event) => setForm({ ...form, rsvped_on: event.target.value })} /></label>
        <label>Guests<input type="number" min="0" value={form.guests} onChange={(event) => setForm({ ...form, guests: event.target.value })} /></label>
        <label>Status<select value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value })}>{ATTENDEE_STATUSES.map((item) => <option key={item}>{item}</option>)}</select></label>
        <label className="meetup-wide">Purpose<input value={form.joining_purpose || ''} onChange={(event) => setForm({ ...form, joining_purpose: event.target.value })} /></label>
        <label className="meetup-wide">WhatsApp consent<input value={form.whatsapp_consent || ''} onChange={(event) => setForm({ ...form, whatsapp_consent: event.target.value })} /></label>
        <label className="meetup-check"><input type="checkbox" checked={Boolean(form.event_host)} onChange={(event) => setForm({ ...form, event_host: event.target.checked })} /> Event host</label>
      </div><div className="meetup-form-actions"><button type="button" onClick={() => setFormOpen(false)}>Cancel</button><button className="meetup-primary" type="submit">Save attendee</button></div></form></div></div>}
    </section>
  );
}

export default MeetupAttendees;